import { REPORT } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique, sum } from '@utils/summaries';
import { basicSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { styleDateRange } from '@utils/styles';

interface MaterialTypeCutsByResellerProps {
    dateRange: { startDate: string; endDate: string };
}

export const MaterialTypeCutsByReseller = ({ dateRange }: MaterialTypeCutsByResellerProps) => {
    const [range, setRange] = useRange('MaterialTypeCutsByReseller');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_MATERIAL_TYPE_CUTS_BY_RESELLER,
        params: { ...dateRange, range },
    });

    const productNames = Array.from(new Set(data.map((item) => item.productName))).sort();

    const rowsByReseller = data.reduce(
        (acc, item) => {
            const existing = acc[item.resellerName] || {
                totalCuts: item.totalCuts,
                products: {} as Record<string, number>,
            };
            existing.totalCuts = item.totalCuts;
            existing.products[item.productName] = item.productPct;
            acc[item.resellerName] = existing;
            return acc;
        },
        {} as Record<
            string,
            {
                totalCuts: number;
                products: Record<string, number>;
            }
        >
    );

    const tableData = Object.entries(rowsByReseller).map(([resellerName, { totalCuts, products }]) => [
        resellerName,
        totalCuts,
        ...productNames.map((name) => products[name] ?? 0),
    ]);

    const headers = ['Reseller Name', 'Total Cuts', ...productNames.map((name) => `${name} %`)];
    const summaries = [countUnique, sum, ...productNames.map(() => average)];
    const sort = [basicSort, basicSort, ...productNames.map(() => basicSort)];

    return (
        <BaseReport
            title={'Material Type Cuts by Reseller'}
            description={
                'This table shows you the % breakdown of each material type by Reseller for the time period selected in the date range above.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            setRange={setRange}
            range={range}
            tableOptions={{
                headers,
                data: tableData,
                summaries,
                sort,
            }}
        />
    );
};
